import { useState } from 'react';
import { TopBar } from '../../components/TopBar';
import { SiteFooter } from '../../components/SiteFooter';
import { HeroSwoosh } from '../../components/HeroSwoosh';
import { FeaturedRow } from './FeaturedRow';
import { NexusAboutModal } from './NexusAboutModal';
import { ToolCardMono } from './ToolCardMono';
import { TOOLS, type Tool } from './tools';
import type { HubUser } from '../../lib/auth';
import { ACCREDITATIONS, COMPANY, yearsTrading } from '../../lib/company';

type Props = {
  user: HubUser;
  appRoles: string[];
  accent: string;
  onSignOut: () => void;
  onLaunch: (tool: Tool) => void;
};

function userHasAccess(tool: Tool, appRoles: string[]): boolean {
  if (!tool.requiredAppRoles || tool.requiredAppRoles.length === 0) return true;
  return tool.requiredAppRoles.some((r) => appRoles.includes(r));
}

export function Hub({ user, appRoles, accent, onSignOut, onLaunch }: Props) {
  const featured = TOOLS.find((t) => t.featured);
  const others = TOOLS.filter((t) => !t.featured);
  const liveCount = TOOLS.filter((t) => t.status !== 'soon').length;
  const [aboutOpen, setAboutOpen] = useState(false);

  return (
    <div className="fade-in" style={{ minHeight: '100vh', background: 'var(--cream)' }}>
      <section style={{ position: 'relative', height: 520, color: '#fff', overflow: 'hidden' }}>
        <div className="scene" />
        <div className="horizon" />
        <HeroSwoosh />
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, rgba(8,8,14,.45) 0%, rgba(8,8,14,.15) 40%, rgba(8,8,14,.55) 100%)',
          }}
        />
        <TopBar tone="dark" user={user} />
        <div
          style={{
            position: 'absolute',
            left: 48,
            right: 48,
            bottom: 54,
            display: 'grid',
            gridTemplateColumns: '1.4fr 1fr',
            gap: 48,
            alignItems: 'end',
          }}
        >
          <div>
            <p className="eyebrow" style={{ color: accent, marginBottom: 14 }}>
              Hi, {user.firstName}
            </p>
            <h1 className="h1" style={{ fontSize: 78, color: '#fff', maxWidth: 980 }}>
              Every J+P tool, in one place.
            </h1>
          </div>
          <div
            style={{
              maxWidth: 420,
              color: 'rgba(255,255,255,.78)',
              fontSize: 15,
              lineHeight: 1.55,
              paddingBottom: 8,
            }}
          >
            <p style={{ margin: 0 }}>
              This is where our staff and clients meet the products we build — from{' '}
              <strong style={{ color: '#fff', fontWeight: 600 }}>Nexus</strong>, the spine of our
              operations, to specialist tools for accessibility, brand and reporting.
            </p>
          </div>
        </div>
      </section>

      <section style={{ padding: '72px 48px 48px' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 24,
            marginBottom: 32,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <p className="eyebrow" style={{ color: accent }}>
              Tools &amp; products
            </p>
            <h2 className="h2" style={{ maxWidth: 760 }}>
              Pick a tool to get started.
            </h2>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span className="mono" style={{ color: 'rgba(14,14,14,.5)' }}>
              {TOOLS.length} tools · {liveCount} live
            </span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18 }}>
          {featured && (
            <div style={{ gridColumn: 'span 2' }}>
              <FeaturedRow
                tool={featured}
                accent={accent}
                onLaunch={onLaunch}
                onAbout={featured.id === 'nexus' ? () => setAboutOpen(true) : undefined}
                hasAccess={userHasAccess(featured, appRoles)}
              />
            </div>
          )}
          {others.map((t) => (
            <ToolCardMono
              key={t.id}
              tool={t}
              accent={accent}
              onLaunch={onLaunch}
              hasAccess={userHasAccess(t, appRoles)}
            />
          ))}
        </div>
      </section>

      <section
        style={{
          padding: '32px 48px 0',
          borderTop: '1px solid rgba(14,14,14,.10)',
          marginTop: 24,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)',
          gap: 48,
          alignItems: 'start',
        }}
      >
        <div>
          <p className="eyebrow" style={{ color: accent }}>
            About {COMPANY.name}
          </p>
          <p className="body muted" style={{ maxWidth: 620, marginTop: 4 }}>
            We have been telling organisations&rsquo; stories from Birmingham since{' '}
            {COMPANY.foundedYear} &mdash; over {yearsTrading()} years. Today {COMPANY.name} is a
            strategic corporate communications agency working with listed and regulated
            organisations on reporting, investor communications, culture and stakeholder engagement.
            These tools are the ones we build to do that work, for our teams and for our clients.
          </p>
        </div>
        <ul
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 7,
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
          {ACCREDITATIONS.map((item) => (
            <li
              key={item}
              className="mono"
              style={{
                border: '1px solid rgba(14,14,14,.16)',
                borderRadius: 3,
                padding: '4px 9px',
                color: 'rgba(14,14,14,.55)',
              }}
            >
              {item}
            </li>
          ))}
        </ul>
      </section>

      <section style={{ padding: '32px 48px 56px' }}>
        <SiteFooter onSignOut={onSignOut} version="v 0.1 · build 26.05" />
      </section>

      {aboutOpen && <NexusAboutModal accent={accent} onClose={() => setAboutOpen(false)} />}
    </div>
  );
}
